/**
 * Scanner & Clues — the anti-stalemate machinery, per player and per team.
 *
 * Wholly ledger-backed, so both halves show an explicit "not recorded" notice
 * for a game predating migration 048 rather than a misleading zero. The clue
 * half is the one that could never be reconstructed after the fact:
 * `clearStalemateTargetReveals` NULLs a team's area and pinpoint on every
 * target re-pick, so the database alone only ever remembers the last one.
 */

import type { TrlClueStats, TrlScannerStats } from '@swtorbit/shared-socket-events';
import { StatCard } from './StatCard';

const CLUE_LABELS: Record<string, string> = {
  area: '🗺️ Area',
  // Retired; still labelled so a past game's history reads correctly.
  compass: '🧭 Compass (retired)',
  scanner: '📡 Scanner',
  pinpoint: '📍 Pinpoint',
};

export function TrlScannerClueCard({
  scanner,
  clues,
}: {
  scanner: TrlScannerStats;
  clues: TrlClueStats;
}) {
  const clueKinds = Object.entries(clues.byKind);

  return (
    <>
      <StatCard
        title="Empowered Scanner"
        icon="📡"
        summary={`${scanner.totalSpent} spent · ${scanner.totalArmed} armed`}
        availability={scanner.availability}
        notRecordedLabel="Scanner use"
        isEmpty={scanner.byPlayer.length === 0}
        emptyMessage="No scanner charges were armed or spent."
      >
        <div className="d-flex flex-column gap-2xs">
          <div className="d-flex gap-xs fs-xs text-slate px-xs">
            <span className="flex-1">Player</span>
            <span style={{ minWidth: '9rem' }}>Team</span>
            <span style={{ minWidth: '4rem' }}>Armed</span>
            <span style={{ minWidth: '4rem' }}>Spent</span>
          </div>
          {scanner.byPlayer.map((p) => (
            <div
              key={p.playerId}
              className="d-flex gap-xs align-items-center px-xs py-2xs rounded-2xs"
              style={{ background: 'color-mix(in srgb, var(--color-white) 4%, transparent)' }}
            >
              <span className="text-white flex-1">{p.characterName}</span>
              <span className="text-cyan fs-sm" style={{ minWidth: '9rem' }}>{p.teamName}</span>
              <span className="text-cyan fs-sm" style={{ minWidth: '4rem' }}>{p.armed}</span>
              <span className="text-white fs-sm" style={{ minWidth: '4rem' }}>{p.spent}</span>
            </div>
          ))}
        </div>
      </StatCard>

      <StatCard
        title="Stalemate Clues"
        icon="🧭"
        summary={`${clueKinds.reduce((sum, [, n]) => sum + n, 0)} handed out`}
        availability={clues.availability}
        notRecordedLabel="Clue delivery"
        isEmpty={clueKinds.length === 0}
        emptyMessage="No team needed a clue — nobody stalled out."
      >
        <div className="d-flex gap-sm flex-wrap">
          {clueKinds.map(([kind, count]) => (
            <div key={kind} className="d-flex flex-column gap-2xs">
              <span className="fs-xs text-slate">{CLUE_LABELS[kind] ?? kind}</span>
              <span className="fs-lg text-white">{count}</span>
            </div>
          ))}
        </div>
      </StatCard>
    </>
  );
}
